import { useMutation, useQuery } from '@apollo/client';
import { App, Button, Card, Col, Form, Row, Typography } from 'antd';
import classNames from 'classnames';
import {
  filter,
  find,
  head,
  isEmpty,
  last,
  map,
  min,
  minBy,
  orderBy,
  set,
  size,
  some,
  toLower,
  uniq,
} from 'lodash-es';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useHistory } from 'react-router';

import {
  QUOTE_ROUTINGS_QUERY,
  SELECT_QUOTE_ROUTING_MUTATION,
} from '../../../app/graphql/quoteQueries';
import routes from '../../../app/routes';
import { useSortToggle } from '../../../common/utils/sortUtils';
import { FAIcon } from '../../../components/adapters/fontAwesomeAdapters';
import IconButton from '../../../components/buttons/IconButton';
import ModalOverlay from '../../../components/dialogs/ModalOverlay';
import ProcessingModal from '../../../components/dialogs/ProcessingModal';
import { CurrencyFormat } from '../../../components/domainSpecific/currencyElements';
import { isQuoteDirectDrive } from '../../../components/quotes/quoteUtils';
import Whitespace from '../../../components/Whitespace';
import { FormItemSearchSingleSelect } from '../../../containers/monitor/monitorFiltersFormItems';
import { FilterPopup } from '../../../containers/monitor/MonitorFilterShared';
import { useMonitorSearchFiltersStyles } from '../../../containers/monitor/MonitorSearchFilters.styles';
import ModalPage from '../../../templates/ModalPage';
import {
  DateFormatMedium,
  TimeFormatShort,
} from '../../../utils/dateFormatting';
import QuoteSummaryOverview from '../../../widgets/quotes/QuoteSummaryOverview';
import { CancelLink } from '../newShipment/newShipmentElements';
import { useNewShipmentElementsStyles } from '../newShipment/newShipmentElements.styles';
import {
  BestRoutingCard,
  DirectDriveCard,
  StandardRoutingCard,
} from './RoutingCards';
import RoutingsFilters from './RoutingsFilter';
import { useQuoteMessage } from '../../../components/dialogs/QuoteRestrictionsMessage';
import { useLayoutElementsStyles } from '../../../components/layout/layoutElements.styles';
import { useQuoteRoutingDialogStyles } from './QuoteRoutingDialog.styles';
import { useQuoteStyles } from '../../../styles/quote.styles';
import { config } from '../../../config/config';

const SORTING_ENUM = {
  RECOMMENDED: 0,
  PRICE: 1,
  DELIVERY_DATE: 2,
  STOP_COUNT: 3,
};

const SORTING_LABELS = {
  [SORTING_ENUM.RECOMMENDED]: 'book.newQuote.routingsSort.recommended',
  [SORTING_ENUM.PRICE]: 'book.newQuote.routingsSort.price',
  [SORTING_ENUM.DELIVERY_DATE]: 'book.newQuote.routingsSort.delivery',
  [SORTING_ENUM.STOP_COUNT]: 'book.newQuote.connectionType',
};

function useRoutingSelect() {
  const { push } = useHistory();
  const [mutate, { loading }] = useMutation(SELECT_QUOTE_ROUTING_MUTATION);

  async function onRoutingSelected(routing) {
    if (!routing) return;

    const response = await mutate({
      variables: {
        referenceNo: routing.referenceNo,
        quoteId: routing.quoteNumber,
      },
    });
    push(
      generatePath(routes.quoting.quoteDetail, {
        referenceNo: response?.data?.selectQuoteRouting?.referenceNo,
      }),
      { newQuote: true, phase2Quote: true }
    );
  }

  return { onRoutingSelected, loading };
}

function getBestValueRouting(routings) {
  if (!some(routings)) return null;
  return minBy(routings, r => r.quoteTotal);
}

function getFastestRouting(routings) {
  if (!some(routings)) return null;
  const earliestArrival = minBy(routings, r => r.deliveryDateTime?.scheduled);
  return earliestArrival || head(routings);
}

function getRecomemendedRouting(routings) {
  return minBy(routings, r => r.rank);
}

function getDriveRouting(routings) {
  return find(routings, r => isQuoteDirectDrive(r));
}

function filterFlightRoutings(routings) {
  return filter(routings, r => !isQuoteDirectDrive(r));
}

function getFlightStops(routing) {
  const flights = routing?.flights;
  if (size(flights) === 1) {
    return 0; // non-stop flight
  }
  return size(uniq(map(flights, f => f.flightNumber)));
}

function sortRoutes(routings, sortField, sortDir) {
  const properDir = toLower(sortDir);
  switch (sortField) {
    case SORTING_ENUM.PRICE: {
      return orderBy(routings, r => r.quoteTotal, properDir);
    }
    case SORTING_ENUM.DELIVERY_DATE: {
      return orderBy(routings, r => r.deliveryDateTime?.scheduled, properDir);
    }
    case SORTING_ENUM.STOP_COUNT: {
      return orderBy(routings, r => getFlightStops(r), properDir);
    }
    default:
      return orderBy(routings, r => r.rank, properDir);
  }
}

function filterRoutes(allRoutes, currentFilter, sortField, sortDir) {
  const isFiltered = route => {
    if (currentFilter.airline) {
      if (!some(route.flights, f => f.airline === currentFilter.airline))
        return false;
    }

    if (currentFilter.departureAirport) {
      if (
        head(route.flights)?.originAirportCode !==
        currentFilter.departureAirport
      )
        return false;
    }

    if (currentFilter.arrivalAirport) {
      if (
        last(route.flights)?.destinationAirportCode !==
        currentFilter.arrivalAirport
      )
        return false;
    }

    return true;
  };

  return sortRoutes(filter(allRoutes, isFiltered), sortField, sortDir);
}

const QuoteRoutingDialog = ({ quote, onBackClick, onError }) => {
  const { message } = App.useApp();
  const { styles } = useQuoteRoutingDialogStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();
  const { styles: sharedStyles } = useQuoteStyles();
  const { styles: monitorStyles } = useMonitorSearchFiltersStyles();
  const { formatMessage: f } = useIntl();
  const [form] = Form.useForm();
  const { showQuoteDisclaimer } = useQuoteMessage();
  const [sortField, setSortField] = useState(SORTING_ENUM.RECOMMENDED);
  const [sortDir, setSortDir] = useState();
  const [filteredQuotes, setFilteredQuotes] = useState();

  const onSortDirChanged = newDir => {
    setSortDir(newDir);
    setFilteredQuotes(sortRoutes(filteredQuotes, sortField, newDir?.order));
  };

  const { icon, onClick } = useSortToggle({
    name: 'sort',
    sort: sortDir,
    setSort: onSortDirChanged,
    alwaysSorted: true,
  });

  const routingsData = useQuery(QUOTE_ROUTINGS_QUERY, {
    variables: { referenceNo: quote?.referenceNo },
  });
  const routings = routingsData?.data?.getQuoteRoutings;
  const flightRoutings = filterFlightRoutings(routings);
  const flightRoutingsPresent = some(flightRoutings);

  const earliestArrival = min(
    map(flightRoutings, r => moment(r.deliveryDateTime?.scheduled))
  );

  const { onRoutingSelected, loading } = useRoutingSelect();

  const bestValueRouting = getBestValueRouting(flightRoutings);
  const fastestRouting = getFastestRouting(flightRoutings);
  const recommendedRouting = getRecomemendedRouting(flightRoutings);
  const driveOnlyRouting = getDriveRouting(routings);

  const pickDateTime = moment(quote?.pickupDateTime?.scheduled);

  useEffect(() => {
    setFilteredQuotes(filterFlightRoutings(routings));
  }, [routings]);

  useEffect(() => {
    if (driveOnlyRouting && isEmpty(flightRoutings)) {
      form.setFieldValue('showDirectDrive', true);
    }
  }, [form, flightRoutings, driveOnlyRouting]);

  useEffect(() => {
    if (config.allowDriveQuote) return;

    if (driveOnlyRouting && isEmpty(flightRoutings)) {
      const errorOverride = set(
        {},
        'graphQLErrors.0.extensions.apiErrors.0.code',
        '999013'
      );
      onError(errorOverride);
      onBackClick();
    }
  }, [driveOnlyRouting, flightRoutings, form, onError, onBackClick]);

  useEffect(() => {
    let key;
    if (flightRoutingsPresent) {
      key = showQuoteDisclaimer(f);
    }
    return () => {
      message.destroy(key);
    };
  }, [showQuoteDisclaimer, message, f, flightRoutingsPresent]);

  const onFilterChanged = newFilter => {
    setFilteredQuotes(
      filterRoutes(flightRoutings, newFilter, sortField, sortDir?.order)
    );
  };

  const onSortChanged = newSort => {
    setSortField(newSort);
    setFilteredQuotes(sortRoutes(filteredQuotes, newSort, sortDir?.order));
  };
  const isDirectDriveVisible = form.getFieldValue('showDirectDrive');

  let isDriveAllowed = !!driveOnlyRouting && flightRoutingsPresent;
  let driveDisabledTooltip = !driveOnlyRouting && (
    <FormattedMessage id="book.newQuote.routingsFilter.disabledDirectDrive" />
  );

  if (!config.allowDriveQuote) {
    isDriveAllowed = false;
    driveDisabledTooltip = null;
  }

  return (
    <ModalOverlay>
      <ModalPage className={styles.quoteRoutingDialog} wrapInScrollbar>
        {routingsData?.loading && (
          <ProcessingModal textId="book.newQuote.optimizing-routing" />
        )}
        <Card
          className={classNames(
            sharedStyles.quoteSummaryHeaderCard,
            styles.quoteSummaryHeaderCardOverride
          )}
        >
          <div
            className={classNames(
              layoutStyles.titleWithExtra,
              styles.quoteRoutingHeaderMain
            )}
          >
            <div className={styles.quoteBackBtnWrap}>
              <Typography.Title level={1} className={sharedStyles.quoteIdLabel}>
                <FormattedMessage id="book.newQuote.routingOptions" />
              </Typography.Title>
              <CancelLink
                messageId="buttons.cancel"
                className={classNames(
                  'text-no-transform',
                  newShipmentElementsStyles.titleCancelLink
                )}
              />
            </div>
            <div>
              <Typography.Title level={2}>
                <FormattedMessage id="book.quote.quoteBreakdown.title" />
              </Typography.Title>
            </div>
          </div>
        </Card>
        <div className="ShipmentDetails">
          <Row className={styles.routingsSummaryReviewRow}>
            <div
              className={classNames(
                'text-uppercase',
                sharedStyles.shipmentInfoTitle,
                styles.shipmentTitleOverride
              )}
            >
              <FormattedMessage id="book.quote.routing.quote-details" />
            </div>
            <Button
              type="link"
              className={styles.backButton}
              onClick={onBackClick}
            >
              <FAIcon icon="chevron-left" />
              <FormattedMessage id="book.newQuote.routingReviewInput" />
            </Button>
          </Row>
          <Card
            className={classNames(
              sharedStyles.quoteSummaryHeaderCard,
              styles.quoteSummaryHeaderCardOverride
            )}
          >
            <Row align="middle" justify="space-between">
              <Col lg={4}>
                <div>
                  <Typography.Title
                    level={3}
                    className={classNames(
                      'text-uppercase',
                      sharedStyles.shipmentStatus,
                      styles.shipmentStatusOverride
                    )}
                  >
                    <FormattedMessage id="book.confirmOrder.pickUpReadyTime" />
                    <div className="text-highlight">
                      <DateFormatMedium value={pickDateTime} />
                      &nbsp;|&nbsp;
                      <TimeFormatShort value={pickDateTime} />
                    </div>
                  </Typography.Title>
                </div>
              </Col>
              <Col span={10} lg={10} xl={13} className="hide-md-and-smaller">
                <QuoteSummaryOverview quote={quote} />
              </Col>
              <Col lg={9} xl={6}>
                <Typography.Title
                  level={3}
                  className={classNames(
                    'text-uppercase',
                    sharedStyles.shipmentStatus,
                    styles.shipmentStatusOverride,
                    styles.shipmentStatusRightOverride
                  )}
                >
                  <FormattedMessage id="book.newQuote.earliestArrivalTime" />
                  <div className="text-highlight">
                    <DateFormatMedium value={earliestArrival} />
                    &nbsp;|&nbsp;
                    <TimeFormatShort value={earliestArrival} />
                  </div>
                </Typography.Title>
              </Col>
            </Row>
          </Card>
        </div>
        <div className="ShipmentDetails">
          <Row className={styles.routingFilterRow}>
            <Col span={24}>
              <RoutingsFilters
                form={form}
                onFilterChanged={onFilterChanged}
                routings={flightRoutings}
                filteredRows={filteredQuotes?.length}
                driveAllowed={isDriveAllowed}
                driveDisabledTooltip={driveDisabledTooltip}
              />
            </Col>
          </Row>
          <Row>
            <div
              className={classNames(
                'text-uppercase',
                sharedStyles.shipmentInfoTitle,
                styles.shipmentTitleOverride
              )}
            >
              <FormattedMessage id="book.newQuote.routingSearchResults" />
            </div>
          </Row>
          {isDirectDriveVisible && (
            <Card className={styles.sectionCard}>
              <p className={styles.sectionBetweenHeader}>
                <FormattedMessage id="book.newQuote.directDrive" />
              </p>
              <Row>
                <Col span={24}>
                  <DirectDriveCard
                    routingOption={driveOnlyRouting}
                    onSelected={onRoutingSelected}
                  />
                </Col>
              </Row>
            </Card>
          )}
          <Card
            className={classNames(styles.sectionCard, {
              [styles.sectionCardSm]: isEmpty(flightRoutings),
            })}
          >
            <p className={styles.sectionBetweenHeader}>
              <FormattedMessage id="book.newQuote.routingFlightOptions" />
            </p>
            {flightRoutingsPresent ? (
              <>
                <Row gutter={[20, 0]} className={styles.sectionRow}>
                  <Col span={24}>
                    <p className={styles.sectionBetweenHeaderAlt}>
                      <FormattedMessage id="book.newQuote.highlightedFlights" />
                    </p>
                  </Col>
                  <Col span={12}>
                    <BestRoutingCard
                      data={bestValueRouting}
                      title={
                        <>
                          <FormattedMessage id="book.newQuote.routingsBestValue" />{' '}
                          |<Whitespace />
                          <CurrencyFormat
                            value={bestValueRouting?.quoteTotal}
                          />
                        </>
                      }
                    />
                  </Col>
                  <Col span={12}>
                    <BestRoutingCard
                      data={fastestRouting}
                      title={
                        <>
                          <FormattedMessage id="book.newQuote.earliestArrival" />{' '}
                          |
                          <Whitespace />
                          <CurrencyFormat value={fastestRouting?.quoteTotal} />
                        </>
                      }
                    />
                  </Col>
                </Row>
                <Row className={styles.sectionRow}>
                  <Col span={24}>
                    <p className={styles.sectionBetweenHeaderAlt}>
                      <FormattedMessage id="book.newQuote.routingRecommended" />
                    </p>
                  </Col>
                  <Col span={24}>
                    <StandardRoutingCard
                      routingOption={recommendedRouting}
                      onSelected={onRoutingSelected}
                      isBestValue={bestValueRouting === recommendedRouting}
                      isEarliestArrival={fastestRouting === recommendedRouting}
                      recommended
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={24} className={styles.routingSortCol}>
                    <p className={styles.sectionBetweenHeaderAlt}>
                      <FormattedMessage id="book.newQuote.routingsAlternate" />
                    </p>
                    <div className={styles.routingsSortLabel}>
                      <IconButton
                        className={classNames(
                          'MonitorSearchFilters-SortIcon',
                          monitorStyles.sortIcon,
                          'active'
                        )}
                        icon={icon}
                        onClick={onClick}
                      />
                      <FilterPopup
                        isPresent={false}
                        label={
                          <div>
                            <FormattedMessage id="book.newQuote.routingsSort.sortBy" />{' '}
                            <FormattedMessage id={SORTING_LABELS[sortField]} />
                          </div>
                        }
                        className={styles.sortPopupLabelNoBackground}
                        rootClassName={styles.sortPopupOverlayFixedWidth}
                        noIcon
                      >
                        <FormItemSearchSingleSelect
                          name="sortBy"
                          formItemComponentProps={{
                            onChange: onSortChanged,
                          }}
                          options={map(
                            [
                              SORTING_ENUM.RECOMMENDED,
                              SORTING_ENUM.DELIVERY_DATE,
                              SORTING_ENUM.PRICE,
                              SORTING_ENUM.STOP_COUNT,
                            ],
                            opt => ({
                              label: f({ id: SORTING_LABELS[opt] }),
                              value: opt,
                            })
                          )}
                        />
                      </FilterPopup>
                    </div>
                  </Col>
                </Row>
                {map(
                  filter(filteredQuotes, q => q !== recommendedRouting),
                  routing => (
                    <Row
                      className={styles.alternateRoutingRow}
                      key={routing?.quoteNumber}
                    >
                      <Col span={24}>
                        <StandardRoutingCard
                          routingOption={routing}
                          onSelected={onRoutingSelected}
                          isBestValue={bestValueRouting === routing}
                          isEarliestArrival={fastestRouting === routing}
                        />
                      </Col>
                    </Row>
                  )
                )}
              </>
            ) : (
              <p>
                <FAIcon icon="exclamation-triangle" />
                <i>
                  <FormattedMessage id="book.newQuote.noFlightsWarning" />
                </i>
              </p>
            )}
          </Card>
        </div>
      </ModalPage>
      {loading && <ProcessingModal textId="book.newQuote.generating-quote" />}
    </ModalOverlay>
  );
};

export default QuoteRoutingDialog;
